// src/modules/inpatients/components/modals/AddRadiologyModal.jsx
import { useEffect, useState } from "react";
import { Info } from "lucide-react";
import Modal from "../shared/Modal";
import { Field, Select, DateInput, TimeInput, DateTimeInput, TextArea } from "../shared/FormFields";
import { PrimaryButton, SecondaryButton } from "../shared/Buttons";
import FileDropzone from "../shared/FileDropzone";
import { staffOptions } from "../../data/mockData";

const TESTS = ["Chest X-Ray (PA View)", "X-Ray Knee", "USG Abdomen", "CT Scan Brain", "MRI Spine", "USG Obstetric", "X-Ray Shoulder", "OPG (Orthopantomogram)"];
const BODY_PARTS = ["Chest", "Knee", "Abdomen", "Brain", "Spine", "Pelvis", "Shoulder", "Dental"];
const PRIORITIES = ["Routine", "Urgent", "STAT"];

const EMPTY = {
  test: "",
  bodyPart: "",
  studyDate: "",
  studyTime: "",
  priority: "",
  reportDateTime: "",
  orderedBy: staffOptions.doctors[0],
  clinicalNotes: "",
  files: [],
};

export default function AddRadiologyModal({ open, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (open) setForm(EMPTY);
  }, [open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const isValid = form.test && form.bodyPart && form.studyDate && form.studyTime && form.reportDateTime && form.orderedBy && form.files.length > 0;

  const addFiles = (fileList) =>
    setForm((f) => ({ ...f, files: [...f.files, ...Array.from(fileList).map((x) => ({ name: x.name }))] }));
  const removeFile = (name) => setForm((f) => ({ ...f, files: f.files.filter((x) => x.name !== name) }));

  const handleSubmit = () => {
    if (!isValid) return;
    onSave(form);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Radiology Report"
      size="lg"
      footer={
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={handleSubmit} disabled={!isValid}>Save Radiology Report</PrimaryButton>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Test / Procedure" required>
          <Select value={form.test} onChange={set("test")}>
            <option value="">Select Test / Procedure</option>
            {TESTS.map((t) => <option key={t} value={t}>{t}</option>)}
          </Select>
        </Field>
        <Field label="Body Part / Region" required>
          <Select value={form.bodyPart} onChange={set("bodyPart")}>
            <option value="">Select Body Part / Region</option>
            {BODY_PARTS.map((b) => <option key={b} value={b}>{b}</option>)}
          </Select>
        </Field>
        <Field label="Study Date" required>
          <DateInput value={form.studyDate} onChange={set("studyDate")} />
        </Field>
        <Field label="Study Time" required>
          <TimeInput value={form.studyTime} onChange={set("studyTime")} />
        </Field>

        <Field label="Priority">
          <Select value={form.priority} onChange={set("priority")}>
            <option value="">Select Priority</option>
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </Select>
        </Field>
        <Field label="Report Date & Time" required>
          <DateTimeInput value={form.reportDateTime} onChange={set("reportDateTime")} />
        </Field>
        <Field label="Ordered By" required className="lg:col-span-2">
          <Select value={form.orderedBy} onChange={set("orderedBy")}>
            {staffOptions.doctors.map((d) => <option key={d} value={d}>{d}</option>)}
          </Select>
        </Field>

        <Field label="Clinical Notes (Optional)" className="lg:col-span-4">
          <TextArea rows={3} placeholder="Enter clinical notes related to this radiology test..." value={form.clinicalNotes} onChange={set("clinicalNotes")} />
        </Field>

        <div className="lg:col-span-4">
          <FileDropzone files={form.files} onFilesAdded={addFiles} onFileRemoved={removeFile} hint="Supports: PDF, JPG, JPEG, PNG (Max. 20MB)" />
        </div>

        <div className="flex items-start gap-2 rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-700 lg:col-span-4">
          <Info size={16} className="mt-0.5 flex-shrink-0" />
          <p><span className="font-medium">Note:</span> Please ensure the uploaded report is clear and contains patient details.</p>
        </div>
      </div>
    </Modal>
  );
}
